import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { getUserAccessToken, refreshAccessToken } from '@sorted/pkce-oauth';
import ReactGA from 'react-ga';
import { ThemeProvider } from '@material-ui/core/styles';
import { sortedTheme } from '@sorted/sorted-ui';
import UserContext from './context/UserContext';
import MicroFrontendContext from './context/MicroFrontendContext';
import AppRoutes from './routing/AppRoutes';
import getEnvironmentConfig from './config/per-environment';
import environmentResolver from './config/resolver';
import settings from './auth/settings';
import mapNewRoleNames from './utils/roleMapper';
import roleSelector from './microfrontends/roleSelector';
import decodeJwtToken from './microfrontends/decodeJwtToken';
import './assets/fonts/fonts.css';

const apiRoles = ['admin', 'read_only', 'user', 'shipper', 'reader'];
const fallbackUserRole = 'read_only';

const ScrollToTop = () => {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	return null;
};

const refreshTokenCallback = () =>
	refreshAccessToken(settings, '/auth/login/go');

const emptyJwtData = {
	environment: environmentResolver(window.location.host),
	user: {
		accessToken: null,
		refreshTokenCallback,
		roles: { admin: false, user: false, readOnly: true }
	}
};

const App = () => {
	const [user, setUser] = useState();
	const environmentConfig = getEnvironmentConfig();
	const accessToken = useRef(null);

	const [jwtData, setJwtData] = useState(emptyJwtData);

	useEffect(() => {
		const token = getUserAccessToken();

		if (token) {
			accessToken.current = token;

			const tokenObject =
				accessToken.current && decodeJwtToken(accessToken.current);

			const roles = roleSelector(
				tokenObject?.Roles,
				apiRoles,
				fallbackUserRole
			);

			const userRoles = roles.reduce(
				(acc, role) => ({ ...acc, [mapNewRoleNames(role)]: true }),
				{
					admin: false,
					user: false,
					readOnly: false
				}
			);

			const userData = {
				...jwtData,
				user: {
					...jwtData.user,
					accessToken: accessToken.current,
					roles: userRoles
				}
			};

			setJwtData({ ...jwtData, ...userData });
		}
	}, [user]);

	ReactGA.initialize(environmentConfig.gaPropertyId);

	return (
		<ThemeProvider theme={sortedTheme}>
			<UserContext.Provider
				value={{
					isAuthenticated: !!user,
					user,
					setUser
				}}
			>
				<MicroFrontendContext.Provider value={jwtData}>
					<Router>
						<ScrollToTop />
						<AppRoutes />
					</Router>
				</MicroFrontendContext.Provider>
			</UserContext.Provider>
		</ThemeProvider>
	);
};

export default App;
